// TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
import type { ReactElement } from "react";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { GraphQLTaggedNode } from "react-relay";
import { graphql, useQueryLoader } from "react-relay";

import {
  ExperimentTitleNames,
  useActiveExperiment,
  useOptimizelyContext,
} from "scmp-app/lib/optimizely";
import type { hooksProteusTopStoriesQuery } from "scmp-app/queries/__generated__/hooksProteusTopStoriesQuery.graphql";
import type { topStoriesHomeV2QueueItemsEdge$key } from "scmp-app/queries/__generated__/topStoriesHomeV2QueueItemsEdge.graphql";

import { HomeV2WithProteusRefetch } from "./proteus-refetch";

const proteusTopStoriesQueryNode: GraphQLTaggedNode = graphql`
  query hooksProteusTopStoriesQuery($topStoriesQueueName: String!) {
    proteusTopStories: queue(filter: { name: $topStoriesQueueName }, variant: 1) {
      # eslint-disable-next-line relay/unused-fields -- consumed via HomeV2WithProteusRefetch
      items(first: 18) {
        # eslint-disable-next-line relay/unused-fields -- consumed via HomeV2WithProteusRefetch
        edges {
          ...topStoriesHomeV2QueueItemsEdge
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
          # eslint-disable-next-line relay/unused-fields -- consumed via HomeV2WithProteusRefetch
          node {
            # eslint-disable-next-line relay/unused-fields -- consumed via HomeV2WithProteusRefetch
            ... on Content {
              entityId
            }
          }
        }
      }
    }
  }
`;

type UseProteusTopStoriesArguments = {
  heroTailEdges: ReadonlyArray<unknown>;
  legacyTopStoriesEdges: topStoriesHomeV2QueueItemsEdge$key;
  renderHeroTopStories: (
    topStoriesEdges: topStoriesHomeV2QueueItemsEdge$key,
    isImpressionSuppressed: boolean,
  ) => ReactElement;
  topStoriesQueueName: string | undefined;
};

type UseProteusTopStoriesResult = {
  legacyIsImpressionSuppressed: boolean;
  proteusElement: null | ReactElement;
};

export const useProteusTopStories = ({
  heroTailEdges,
  legacyTopStoriesEdges,
  renderHeroTopStories,
  topStoriesQueueName,
}: UseProteusTopStoriesArguments): UseProteusTopStoriesResult => {
  const { experiment: proteusExperiment } = useActiveExperiment(ExperimentTitleNames.Proteus);
  const { initialized: optimizelyInitialized } = useOptimizelyContext();
  const isProteusVariant = proteusExperiment?.variation?.name === "1";
  const [proteusQueryReference, loadProteusQuery, disposeProteusQuery] =
    useQueryLoader<hooksProteusTopStoriesQuery>(proteusTopStoriesQueryNode);

  useEffect(() => {
    if (!optimizelyInitialized) return;
    if (!isProteusVariant) return;
    if (!topStoriesQueueName) return;

    loadProteusQuery({ topStoriesQueueName }, { fetchPolicy: "network-only" });

    return () => {
      disposeProteusQuery();
    };
  }, [
    disposeProteusQuery,
    isProteusVariant,
    loadProteusQuery,
    optimizelyInitialized,
    topStoriesQueueName,
  ]);

  const proteusElement = proteusQueryReference ? (
    <ErrorBoundary fallback={renderHeroTopStories(legacyTopStoriesEdges, true)}>
      <Suspense fallback={renderHeroTopStories(legacyTopStoriesEdges, true)}>
        <HomeV2WithProteusRefetch
          heroTailEdges={heroTailEdges}
          proteusQueryReference={proteusQueryReference}
          proteusTopStoriesQueryNode={proteusTopStoriesQueryNode}
        >
          {proteusTopStoriesEdges => renderHeroTopStories(proteusTopStoriesEdges, false)}
        </HomeV2WithProteusRefetch>
      </Suspense>
    </ErrorBoundary>
  ) : null;

  return {
    legacyIsImpressionSuppressed: !optimizelyInitialized || isProteusVariant,
    proteusElement,
  };
};
